import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import NewsLetter from "../elements/NewsLetter";
import PageTitle from "../elements/PageTitle";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const HealthyPackageDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [pricePlans, setPricePlans] = useState({});
  const [pricePlansOriginal, setPricePlansOriginal] = useState({});
  const [promotion, setPromotion] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPackageDetails() {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        if (!token) {
          setError("Please login to view package details");
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_BASE}/courses/Packages/healthy-packages/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.status === 401) {
          setError("Please login to view package details");
          navigate("/login");
          return;
        }

        if (res.status === 403) {
          const errorData = await res.json().catch(() => ({}));
          setError(errorData.error || "Your subscription has expired or been canceled");
          // Redirect to pricing page to renew subscription
          setTimeout(() => {
            navigate(`/Healthy/Healthy-Pricing/${id}`);
          }, 3000);
          return;
        }

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to fetch package details");
        }

        const data = await res.json();
        
        // If user doesn't have subscription, redirect to pricing page with package ID
        if (!data.hasSubscription) {
          navigate(`/Healthy/Healthy-Pricing/${id}`);
          return;
        }

        // Only set data if user has subscription
        setPackageData(data.package);
        setPricePlans(data.pricePlans || {});
        setPricePlansOriginal(data.pricePlansOriginal || {});
        setPromotion(data.promotion);
        setHasSubscription(data.hasSubscription);
        setSubscription(data.subscription);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      fetchPackageDetails();
    }
  }, [id, navigate]);

  // Helper function to convert cents to dollars
  const centsToDollars = (cents) => {
    return (cents / 100).toFixed(2);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format plan name
  const formatPlanName = (plan) => {
    if (plan === 'monthly') return '1 Month';
    if (plan === 'quarterly') return '3 Months';
    return plan || 'N/A';
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container text-center py-5">
 <div
                className="boxes"
                style={{ margin: "40px auto", width: "96px", height: "64px" }}
              >
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="box">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>        </div>
      </div>
    );
  }

  // If no subscription, don't show anything (should have redirected already)
  if (!hasSubscription) {
    return (
      <div className="page-content">
        <div className="container text-center py-5">
          <p>Redirecting to pricing page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container text-center py-5">
          <p className="text-danger">{error}</p>
          <Link to="/Healthy" className="btn btn-primary mt-3">
            Back to Healthy Packages
          </Link>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="page-content">
        <div className="container text-center py-5">
          <p>Package not found</p>
          <Link to="/Healthy" className="btn btn-primary mt-3">
            Back to Healthy Packages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ backgroundColor: "#fdffa2ff" }}>
      <PageTitle parentTitle="Healthy Package Details" />

      <section className="content-inner">
        <div className="container">
          {/* Subscription Status Banner - Only show if has subscription */}
          {hasSubscription && subscription && (
            <div className="alert alert-success mb-4">
              <strong>✓ Active Subscription</strong>
              <br />
              Plan: <strong>{formatPlanName(subscription.plan)}</strong> | Expires:{" "}
              <strong>{formatDate(subscription.current_period_end)}</strong>
            </div>
          )}

          {/* Package Header */}
          <div className="row mb-4">
            <div className="col-md-12">
              <h1 className="mb-3">{packageData.name}</h1>
              <p className="lead">{packageData.description}</p>
              <p>
                <strong>Duration:</strong> {packageData.duration_days} days
              </p>
            </div>
          </div>

          {/* Package Images Gallery */}
          {packageData.images && packageData.images.length > 0 && (
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="row">
                  {packageData.images.map((image, index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <img
                        src={image.url}
                       
                        className="img-fluid rounded"
                        style={{ width: "100%", height: "250px", objectFit: "cover" }}
                      />
                     
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Features Section - Only show if user has subscription */}
          {hasSubscription && (
            <div className="row mb-4">
              <div className="col-12">
                <h3 className="mb-4 text-center">Package Features</h3>
                {packageData.features && packageData.features.length > 0 ? (
                  <div className="row justify-content-center">
                    {packageData.features.map((feature, index) => (
                      <div 
                        key={index} 
                        className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 mb-3"
                        style={{ 
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        <div className="card h-100" style={{ width: '100%', maxWidth: '500px' }}>
                          <div className="card-body text-center">
                            <h5 className="card-title">{feature.title}</h5>
                            <p className="card-text">{feature.details}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center">No features available for this package.</p>
                )}
              </div>
            </div>
          )}

         

          {/* Action Buttons */}
          <div className="row mb-4">
            <div className="col-md-12 text-center">
              <Link to="/Healthy" className="btn btn-secondary">
                Back to Packages
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="call-action style-1 footer-action">
        <div className="container">
          <NewsLetter />
        </div>
      </section>
    </div>
  );
};

export default HealthyPackageDetails;

